import { z } from 'zod';

/**
 * The call playbook — what the voice agent is CONTRACTED to do on a call.
 *
 * Lives in shared because it has two consumers that must not drift:
 *   - the voice runtime, which stamps every agent message with a step from
 *     {@link CALL_TASK_KIND} and records the workflow it ran
 *     ({@link WORKFLOW_RAN_EVENT}, apps/livekit/src/agents/runtime/workflow/
 *     attribution.ts), and
 *   - the post-call evaluator, which judges "Followed your playbook" against
 *     that record (apps/api/src/core/call-evaluation/, ADR-312).
 *
 * Before this module the evaluator had no way to know what the agent was told
 * to do — it could judge hallucination and dead air from the transcript, but
 * "did it collect the required details" and "did it say the new-patient
 * reminders" were unanswerable. The API and worker cannot import from
 * apps/livekit, so the contract has to live here to be checkable at all.
 *
 * ⚠️ PROMPT SURFACE. {@link NEW_PATIENT_REMINDERS} reaches patients verbatim
 * (the SMS seed's `reminders` slot) and {@link OUTBOUND_REFERRAL_REMINDERS} is
 * what voice calls speak, so both are governed by
 * apps/livekit/src/agents/runtime/PRINCIPLES.md and the `/check-prompts` skill.
 * Editing a line here changes what patients hear.
 *
 * ── Tenancy ──────────────────────────────────────────────────────────────
 * Everything here is currently GLOBAL — the same playbook for every practice.
 * The intended end state is per-tenant config (`PracticeAgentConfig` in
 * @vagusx/agent-core already models it: `personaName`, `contentSlots` with a
 * tenant-vs-internal `authorScope`, an ordered block flow), but that config is
 * wired only to apps/sms-agent today; nothing under apps/livekit reads it.
 * When it is wired up, only the evaluator's `buildPlaybookFacts` changes —
 * the rubric judges against whatever this module reports, so swapping the
 * source does not touch the rubric.
 */

/**
 * Every step a workflow node's speech can be stamped with — the playbook's
 * vocabulary. A node maps to one in `workflow/attribution.ts`. The names are
 * v1's task kinds, kept so a call's steps read the same before and after it.
 */
export const CALL_TASK_KIND = [
  'opener',
  'identity',
  'disclosure',
  'confirm_details',
  'scheduling',
  'confirmation',
  'reminders',
  'anything_else',
  'take_message',
  'cancel_booking',
  'goodbye',
] as const;

export type CallTaskKind = (typeof CALL_TASK_KIND)[number];

/**
 * The steps a call is EXPECTED to run, in this order, whenever its workflow
 * contains them — the spine the post-call evaluator reports as executed or
 * missed.
 *
 * THE ORDER IS THE PLAYBOOK'S, NOT THE WALK'S. A step can span several nodes
 * in different places: inbound's `find_appointment` (which appointment to move)
 * is `scheduling` and sits before `confirm_details`, while the `schedule` that
 * books sits after it. Ordering by where a step's first node falls would tell
 * the judge inbound schedules before it confirms details. The workflow decides
 * WHICH of these apply; this list decides the order they are expected in.
 *
 * Every other step happens because of something said on the call: a booking
 * (`confirmation`, `reminders`, `anything_else`), a caller who wants a person
 * or a message (`take_message`), a cancellation (`cancel_booking`), an ending
 * (`goodbye`). A workflow has nodes for them, but a call that never needed one
 * did not miss it — so they are counted when they run and never as missed.
 *
 * A workflow need not contain all five. `verify-caller-v2` schedules nothing,
 * so `scheduling` is not expected of it; inbound has no clinical reason to
 * disclose, so `disclosure` is not expected there.
 *
 * Nor need a call run every spine step its workflow contains. A step whose
 * nodes this call's own facts gated shut is not expected of it either (see
 * {@link gatedStepsSchema}, ADR-362).
 */
export const SPINE_STEPS: readonly CallTaskKind[] = [
  'opener',
  'identity',
  'disclosure',
  'confirm_details',
  'scheduling',
];

/**
 * The steps a call's own facts ruled out (ADR-362): the walk reached the step
 * and every one of its nodes was skipped by its gate (`requires`/`unless`), so
 * none of them ran. Recorded by the voice runtime at teardown in
 * `call_result.workflow.gatedSteps` (`gatedSteps`,
 * apps/livekit/src/agents/runtime/workflow/attribution.ts), from the same
 * node → step map that stamps every agent message.
 *
 * WHY IT EXISTS. `workflow:ran` lists the steps a workflow CONTAINS, which is
 * all a call knows when it starts. The inbound flow contains the identity gate,
 * the details read-back and the booking, and a caller who rang to leave a
 * message (ADR-241) or whose questions the front desk settled (ADR-361)
 * reaches none of them by design. Without this the evaluator listed them as
 * missed, and QA int `c07b0a3a` was graded down for skipping identity
 * verification on a call that had nothing to verify.
 *
 * A step the walk never reached — the caller hung up first — is NOT gated. It
 * stays expected, and the judge explains the miss against how the call ended.
 */
export const gatedStepsSchema = z.array(z.enum(CALL_TASK_KIND));

/**
 * The steps this call was expected to run: the spine steps its workflow
 * contains, less the ones its facts gated out, in spine order.
 *
 * `gatedSteps` defaults to none, which is how a call recorded before ADR-362
 * is judged: against every spine step its workflow contains, as before.
 */
export function expectedStepsOf(
  workflowSteps: readonly CallTaskKind[],
  gatedSteps: readonly CallTaskKind[] = []
): CallTaskKind[] {
  return SPINE_STEPS.filter((step) => workflowSteps.includes(step) && !gatedSteps.includes(step));
}

/**
 * The interaction event that records which workflow a call ran (ADR-312).
 *
 * Written by the voice runtime once the workflow is built, for every call —
 * code-defined, published or a builder test. The post-call evaluator judges the
 * call against it. Both sides read this module, so neither can name the event
 * or shape its payload differently.
 *
 * `steps` is computed by the runtime from the same node → step map it stamps
 * every agent message with (`workflow/attribution.ts`), so what the evaluator
 * expects and what it counts as run cannot use two vocabularies. It is fixed
 * when the call starts: re-evaluating the call after a later deploy changed
 * the definitions still judges it against the workflow it actually ran.
 */
export const WORKFLOW_RAN_EVENT = 'workflow:ran';

export const WORKFLOW_SOURCE = ['code', 'published', 'test_snapshot'] as const;
export type WorkflowSource = (typeof WORKFLOW_SOURCE)[number];

export const workflowRanSchema = z.object({
  /** Where the definition came from. */
  source: z.enum(WORKFLOW_SOURCE),
  /** The definition id — `outbound-referral-v1`, or a practice workflow's key. */
  key: z.string().min(1),
  version: z.number().int(),
  /** `workflow_defn_versions.id` or `workflow_defn_snapshots.id`; null for code. */
  versionId: z.string().nullable(),
  /** Every step the workflow's reachable nodes speak as, once each, in definition order. */
  steps: z.array(z.enum(CALL_TASK_KIND)),
  /**
   * Nodes the step map does not name. Their speech is stamped with no step, so
   * a step they run never counts as run. Node ids only — never content.
   */
  unattributedNodes: z.array(z.string()),
});
export type WorkflowRan = z.infer<typeof workflowRanSchema>;

/**
 * The locked, verbatim new-patient reminder block, sent after a booking
 * succeeds — in full by SMS; voice speaks {@link OUTBOUND_REFERRAL_REMINDERS}. One spoken line per entry — kept discrete
 * rather than one paragraph so a practice can later drop or reorder individual
 * reminders without re-splitting prose.
 *
 * Global today, and the CT-scan imaging note in particular is specialty-
 * specific — a per-practice config should own these (see the tenancy note at
 * the top of this file).
 *
 * THE ARRIVAL LINE IS CONDITIONAL ("if this is your first visit"), not an
 * assertion ("because you are a new patient"). `isNewPatient` is genuinely
 * UNKNOWN on most calls that reach this block — the delivery gate is
 * `isNewPatient !== false`, which fires on `null` too — so the asserting form
 * told established callers booking a follow-up to arrive thirty minutes early
 * for intake paperwork they do not need. Corrected in ADR-146; do not revert it
 * here without reverting it there. `reminders-parity.test.ts` pins the seeded
 * SMS content slot to it.
 */
const REMINDER_ARRIVE_EARLY =
  'If this is your first visit with us, please arrive thirty minutes before your appointment so we can complete your intake paperwork.';
const REMINDER_BRING_DOCUMENTS =
  'Please bring your photo ID, your insurance card, and a list of the medications you currently take.';
const REMINDER_BRING_IMAGING =
  'If you have had a recent CT scan, please bring the images on a disc along with the written report if you can.';

export const NEW_PATIENT_REMINDERS: readonly string[] = [
  REMINDER_ARRIVE_EARLY,
  REMINDER_BRING_DOCUMENTS,
  REMINDER_BRING_IMAGING,
];

/**
 * The reminders voice calls speak, inbound and outbound — `NEW_PATIENT_REMINDERS`
 * without the imaging line (ADR-247). The post-call evaluator grades outbound
 * referral calls against this, and `outbound-referral.test.ts` pins the flow's
 * slots to it.
 */
export const OUTBOUND_REFERRAL_REMINDERS: readonly string[] = [
  REMINDER_ARRIVE_EARLY,
  REMINDER_BRING_DOCUMENTS,
];

/**
 * The chart-accuracy details the `confirm_details` node reads back for the
 * patient to confirm or correct (the v2 `ConfirmDetails` component's fields in
 * `workflow/definitions.ts`) — the evaluator needs the labels to judge whether
 * each was actually covered.
 *
 * `address` and `insurance` are skipped when nothing is on file, so a call
 * that never mentions them is not automatically a miss — the evaluator is told
 * which ones were in play for THIS call.
 */
export const REQUIRED_DETAIL_FIELDS: readonly { id: string; label: string }[] = [
  { id: 'address', label: 'home address' },
  { id: 'insurance', label: 'insurance' },
  { id: 'phone', label: 'phone number' },
];
